import { Injectable, OnDestroy } from '@angular/core';
import { Router, ResolveEnd, ActivatedRouteSnapshot, NavigationEnd, ActivatedRoute } from '@angular/router';

import { Subscription, Observable, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserType } from '@enums';
import { Customer, Permission } from '@model';
import { SubSink } from 'subsink';
import * as fromCustomersStore from '@appStore/customers';
import { Store } from '@ngrx/store';
import { State as userInfoState } from '@appStore/account/reducers/user-info.reducer';

import {State as configurationState, getPermissionsWithLoaded } from '@appStore/configuration/reducers/index';
import {State as accountState, getUserInfoState} from '@appStore/account/reducers/index';
import { SxApplicationInsightsService } from './sx-application-insights.service';

//declare const gtag: Function;
@Injectable()
export class SxMonitoringService implements OnDestroy {
  private routerSubscription: Subscription;
  private instrumentationEnabled: boolean;
  private analyticsEnabled: boolean;
  private analytics4Enabled: boolean;
  private analytics4Key: string;

  private subs = new SubSink();
  customerIdsForUser: string;
  userType: UserType;
  extendedApplicationsInsightsTracing: boolean;
  pageTraceSubmitted: boolean;
  userType$: Observable<UserType>;
  userInfo$: Observable<userInfoState>;
  hotjarInitialized = false;
  permissions$: Observable<{ permissions: Permission[]; loaded: boolean; }>;

  constructor(
    private router: Router,
    private accountStore: Store<accountState>,
    private configurationStore: Store<configurationState>,
    private customerStore: Store<fromCustomersStore.CustomersState>,
    private aiService: SxApplicationInsightsService
    ) {
    this.userInfo$ = this.accountStore.select(getUserInfoState);
    this.permissions$ = this.configurationStore.select(getPermissionsWithLoaded);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  initService(instrumentationKey: string, analytics4Key: string, extendedApplicationsInsightsTracing: boolean) {
    if (this.routerSubscription) {
      return;
    }

    if (!instrumentationKey && !analytics4Key) {
      return;
    }

    // Set up Application Insights
    if (instrumentationKey) {
      this.aiService.initService(instrumentationKey);
      this.instrumentationEnabled = true;
      this.extendedApplicationsInsightsTracing = extendedApplicationsInsightsTracing;
    }

    // Google Analytics - UA
    // if (analyticsKey) {
    //   (<any>window).ga('create', analyticsKey, 'auto');
    //   this.analyticsEnabled = true;
    // }

    // Google Analytics 4
    if (analytics4Key) {
      this.analytics4Key = analytics4Key;
      (<any>window).gtag('config', this.analytics4Key, { 'send_page_view': false });
      let initializedWithUserId = false;
      this.subs.sink = this.userInfo$.pipe(filter(state => state.loaded)).subscribe(userState => {
        if (!initializedWithUserId) {
          (<any>window).gtag('config', this.analytics4Key, { 
            'user_id': userState.userinfo.id,
            'send_page_view': false 
          });
          initializedWithUserId = true;
        }
      });
      this.analytics4Enabled = true;
    }
    
    // this.routerSubscription = this.router.events
    //   .pipe(filter(event => event instanceof ResolveEnd))
    //   .subscribe((event: ResolveEnd) => {
    //     const activatedComponent = this.getActivatedComponent(event.state.root);
    //     if (activatedComponent) {
    //       let compName: string = activatedComponent.name.replace('Component', '');
    //       this.logPageView(`${compName} - ${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
    //       this.logGa4PageView(compName, event);
    //     }
    //   });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd),
        map((x: NavigationEnd) => {
          let urlAfterRedirects = x.urlAfterRedirects;
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          let _data: any;
         
          while (route!.firstChild) {
            route = route.firstChild;
          }
          // if (route!.snapshot.routeConfig && route!.snapshot.routeConfig.title) {
          //   routeTitle = route!.snapshot.routeConfig.title!.toString();
          // }
          if (route!.snapshot.routeConfig && route!.data) {
            route!.data.subscribe(data => _data = data);
            routeTitle = _data.name;
          }
          return {routeTitle, urlAfterRedirects};
        })
      )
      .subscribe((_) => {
        if (_.routeTitle || _.urlAfterRedirects) {
          let compName: string = _.routeTitle?.replace('Component', '');
          this.logPageView(`${compName} - ${_.urlAfterRedirects}`, _.urlAfterRedirects);
          this.logGa4PageView(compName, _.urlAfterRedirects);
        }
      });

    // Hotjar
      this.setHotjarUserAttributes();
  }

  setHotjarUserAttributes() {
    this.subs.sink = combineLatest(this.userInfo$, this.permissions$)
    .pipe(
      map((value: [userInfoState, { permissions: Permission[]; loaded: boolean; }]) => ({
        userinfo: value[0],
        permissionsAndLoaded: value[1]
      }))
    )
    .subscribe((x) => {
      if (x.userinfo.loaded &&  x.userinfo.userinfo && 
        !this.hotjarInitialized && x.userinfo.userType && x.permissionsAndLoaded.loaded) {
        let permissions = x.permissionsAndLoaded.permissions.filter(p => p.customers.length !== 0).map(p => p.name.toString());
        let permissionsAsString = permissions.join(',');
        (<any>window).hj('identify', x.userinfo.userinfo.id, {
              'currentSegment': x.userinfo.userType,
              'name': x.userinfo.userinfo.name,
              'email': x.userinfo.userinfo.email,
              'permissions': permissionsAsString,
              'register_date': x.userinfo.userinfo.registerDate,
              'impersonated': x.userinfo.userinfo.impersonatedAsName ? 'Yes' : 'No'
          });
          this.hotjarInitialized = true;
      }
    });
  }

  // Set AI UserContext
  setAuthenticatedUserId(userId: string): void {
    this.aiService.setAuthenticatedUserId(userId);
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += '/' + snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  // public logGa4PageView(title: string, event: ResolveEnd) {
  //   if (!this.analytics4Enabled) return;

  //   console.log(`sxMonitoringService\tga4PageView logged ${title}`, event.urlAfterRedirects);

  //   this.subs.sink = this.userInfo$.pipe()
  //   .subscribe(state => {
  //     if (!state.userinfo) {
  //       (<any>window).gtag('event', 'page_view', {
  //         title: title,
  //         page_title: title,
  //         page_location: window.location.href,
  //         page_path: event.urlAfterRedirects,
  //         send_to: this.analytics4Key
  //       });
  //       return;
  //     }

  //     (<any>window).gtag('event', 'page_view', {
  //       title: title,
  //       page_title: title,
  //       page_location: window.location.href,
  //       page_path: event.urlAfterRedirects,
  //       send_to: this.analytics4Key
  //     });
  //    });
  // }

  public logGa4PageView(title: string, urlAfterRedirects: string) {
    if (!this.analytics4Enabled) {
      return;
    }

    //console.log(`sxMonitoringService\tga4PageView logged ${title}`, urlAfterRedirects);

    this.subs.sink = this.userInfo$.pipe()
    .subscribe(state => {
      if (!state.userinfo) {
        (<any>window).gtag('event', 'page_view', {
          title: title,
          page_title: title,
          page_location: window.location.href,
          page_path: urlAfterRedirects,
          page_url: urlAfterRedirects,
          send_to: this.analytics4Key
        });  

        return;
      }

      (<any>window).gtag('event', 'page_view', {
        title: title,
        page_title: title,
        page_location: window.location.href,
        page_path: urlAfterRedirects,
        page_url: urlAfterRedirects,
        send_to: this.analytics4Key
      });

     });
  }

  public logGa4Event(name: string, data?: any) {
    if (!this.analytics4Enabled) {
      return;
    }

    (<any>window).gtag('event', name, data ? data : {});
    //console.log(`sxMonitoringService\tga4event logged ${name}`, data);
  }

  // Logg Extended attributes to Application Insight
  public logPageView(
    name: string,
    url?: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number },
    duration?: number
  ) {
    if (url && url.length > 1000) {
      url = url.substring(0, 1000);
    }

    if (!properties) {
      properties = {};
     }

    this.pageTraceSubmitted = false;

    if (this.extendedApplicationsInsightsTracing) {

      this.subs.sink = combineLatest(this.customerStore.select(fromCustomersStore.getCustomers),
        this.userType$, this.customerStore.select(fromCustomersStore.getCustomersLoaded))
        .pipe(
          map((value: [Customer[], UserType, boolean]) => ({
            customers: value[0],
            userType: value[1],
            customersLoaded: value[2]
          })),
          filter((cl) => cl.customersLoaded && !!cl.userType),
          take(1)
        )
        .subscribe((x) => {
            properties['customerIdsForUser'] = x.customers.map(c => c.id) .join(';');
            properties['userType'] = x.userType;
            // Logg to Application Insight
            this.aiService.logPageView(name, url, properties, measurements, duration);
            this.pageTraceSubmitted = true;
          });
      } else {
        // Logg to Application Insight
        this.aiService.logPageView(name, url, properties, measurements, duration);
        this.pageTraceSubmitted = true;
    }

    // Google Analytics
    if (this.analyticsEnabled) {
      (<any>window).ga('set', 'page', url);
      (<any>window).ga('send', 'pageview');
    }
  }

  // Logg Extended attributes to Application Insight
  public logNonExtendableEvent(name: string, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
  
    // Logg to Application Insight
    this.aiService.logEvent(name, properties, measurements);
   
  }

   // Logg Extended attributes to Application Insight
   public logEvent(name: string, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
    if (this.extendedApplicationsInsightsTracing) {

      if (!properties) {
        properties = {};
      }

      this.subs.sink = combineLatest([this.customerStore.select(fromCustomersStore.getCustomers),
        this.userType$, this.customerStore.select(fromCustomersStore.getCustomersLoaded)])
        .pipe(
          map((value: [Customer[], UserType, boolean]) => ({
            customers: value[0],
            userType: value[1],
            customersLoaded: value[2]
          })),
          filter((cl) => cl.customersLoaded && !!cl.userType),
         take(1)
        )
        .subscribe((x) => {
          properties['customerIdsForUser'] = x.customers.map(c => c.id) .join(';');
          properties['userType'] = x.userType;

          // Logg to Application Insight
          this.aiService.logEvent(name, properties, measurements);
      });
    } else {
      // Logg to Application Insight
      this.aiService.logEvent(name, properties, measurements);
    }
  }
}
